if (!customElements.get('card-image-carousel')) {
  customElements.define(
    'card-image-carousel',
    class CardImageCarousel extends HTMLElement {
      constructor() {
        super();

        this.current = parseInt(this.dataset.current, 10) || 0;
        this.total = parseInt(this.dataset.total, 10) || 1;

        this.track = this.querySelector('.collection-inspired-card__track');
        this.prevButton = this.querySelector('[data-prev]');
        this.nextButton = this.querySelector('[data-next]');
        this.bullets = Array.from(this.querySelectorAll('[data-pagination] > span'));

        if (!this.track || this.total <= 1) {
          return;
        }

        this.onPrev = this.handlePrev.bind(this);
        this.onNext = this.handleNext.bind(this);

        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);

        // --- Swipe tactile ---------------------------------------------------
        // Lien produit qui enveloppe les slides : on doit neutraliser sa
        // navigation uniquement quand un vrai glissement a eu lieu.
        this.link = this.querySelector('.collection-inspired-card__media');

        this.suppressClick = false;
        this.pointerId = null;
        this.dragging = false;
        this.axis = null;

        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onPointerCancel = this.handlePointerCancel.bind(this);
        this.onLinkClick = this.handleLinkClick.bind(this);

        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.link?.addEventListener('click', this.onLinkClick, true);

        this.update();
      }

      disconnectedCallback() {
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('pointerdown', this.onPointerDown);
        this.link?.removeEventListener('click', this.onLinkClick, true);
        this.removeDragListeners();
      }

      handlePrev(event) {
        event.preventDefault();
        event.stopPropagation();
        this.go(this.current - 1);
      }

      handleNext(event) {
        event.preventDefault();
        event.stopPropagation();
        this.go(this.current + 1);
      }

      // --- Gestes tactiles ---------------------------------------------------

      addDragListeners() {
        this.track?.addEventListener('pointermove', this.onPointerMove);
        this.track?.addEventListener('pointerup', this.onPointerUp);
        this.track?.addEventListener('pointercancel', this.onPointerCancel);
      }

      removeDragListeners() {
        this.track?.removeEventListener('pointermove', this.onPointerMove);
        this.track?.removeEventListener('pointerup', this.onPointerUp);
        this.track?.removeEventListener('pointercancel', this.onPointerCancel);
      }

      handlePointerDown(event) {
        // La souris garde le comportement bureau (flèches + survol).
        if (event.pointerType === 'mouse') return;

        // Carte dans un slider horizontal qui déborde (carrousel mobile ou
        // desktop) : le swipe doit faire défiler le slider, pas les photos de
        // la carte — deux swipes horizontaux imbriqués se neutralisent. Les
        // chevrons restent disponibles pour changer de photo. Le CSS lève
        // `touch-action: pan-y` dans ce même contexte.
        const slider = this.closest('slider-element');
        if (slider && slider.scrollWidth > slider.clientWidth) return;

        this.suppressClick = false;
        this.pointerId = event.pointerId;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.startTime = performance.now();
        this.dragging = false;
        this.axis = null;

        this.addDragListeners();
      }

      handlePointerMove(event) {
        if (event.pointerId !== this.pointerId) return;

        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;

        if (!this.axis) {
          if (Math.hypot(dx, dy) <= 6) return;

          // Biais horizontal : un geste ambigu (diagonale) reste au scroll page.
          if (Math.abs(dx) > Math.abs(dy) * 1.2) {
            this.axis = 'x';
            this.dragging = true;
            try {
              this.track.setPointerCapture(this.pointerId);
            } catch (e) {
              /* capture indisponible : on continue sans */
            }
          } else {
            // Geste vertical : on rend la main au défilement de la page.
            this.axis = 'y';
            this.endGesture();
            return;
          }
        }

        if (this.axis === 'x') {
          event.preventDefault();
        }
      }

      handlePointerUp(event) {
        if (event.pointerId !== this.pointerId) return;

        if (this.dragging) {
          const dx = event.clientX - this.startX;
          const elapsed = performance.now() - this.startTime;
          const threshold = Math.max(40, this.track.clientWidth * 0.18);
          const flick = elapsed < 250 && Math.abs(dx) > 12;

          if (Math.abs(dx) >= threshold || flick) {
            // « Suivre le doigt » : glisser vers la gauche révèle l'image
            // suivante. Volontairement indépendant de RTL — le transform
            // translateX(-N%) et les flèches (handleNext = current + 1) sont eux
            // aussi physiquement LTR, donc le swipe doit suivre la même
            // convention pour rester cohérent avec eux et avec le mouvement réel
            // à l'écran.
            if (dx < 0) {
              this.go(this.current + 1);
            } else {
              this.go(this.current - 1);
            }
          }

          // Un glissement a eu lieu : on bloque l'ouverture du produit pour ce
          // geste, puis on réarme le drapeau au cas où iOS n'émettrait pas le
          // click final (sinon il « mangerait » le tap suivant).
          this.suppressClick = true;
          requestAnimationFrame(() => {
            this.suppressClick = false;
          });
        }

        this.endGesture();
      }

      handlePointerCancel(event) {
        if (event.pointerId !== this.pointerId) return;
        this.endGesture();
      }

      endGesture() {
        if (this.pointerId !== null) {
          try {
            this.track.releasePointerCapture(this.pointerId);
          } catch (e) {
            /* rien à libérer */
          }
        }
        this.removeDragListeners();
        this.pointerId = null;
        this.dragging = false;
        this.axis = null;
      }

      handleLinkClick(event) {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        }
      }

      go(index) {
        if (index < 0) index = 0;
        if (index > this.total - 1) index = this.total - 1;
        if (index === this.current) return;

        this.current = index;
        this.update();
      }

      update() {
        if (this.track) {
          this.track.style.transform = 'translateX(-' + this.current * 100 + '%)';
        }

        if (this.prevButton) {
          this.prevButton.disabled = this.current === 0;
        }

        if (this.nextButton) {
          this.nextButton.disabled = this.current === this.total - 1;
        }

        this.bullets.forEach((bullet, index) => {
          if (index === this.current) {
            bullet.setAttribute('data-active', '');
          } else {
            bullet.removeAttribute('data-active');
          }
        });
      }
    }
  );
}

document.addEventListener('click', (event) => {
  const swatch = event.target.closest('.collection-inspired-card__swatch[data-carousel-index]');
  if (!swatch) return;

  const card = swatch.closest('.collection-inspired-card');
  const carousel = card?.querySelector('card-image-carousel');
  const index = parseInt(swatch.dataset.carouselIndex, 10);

  if (!carousel || Number.isNaN(index)) return;

  carousel.go(index);
});
